import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Tre stati distinti: mittente non collegato ≠ nessun template approvato.
const TPL_REASON_KEYS = {
  RECEPTIONIST_WHATSAPP_NOT_CONNECTED: "whatsappChat.reason.receptionistNotConnected",
  RECEPTIONIST_NOT_ASSIGNED: "whatsappChat.reason.receptionistNotAssigned",
  WHATSAPP_NOT_CONNECTED: "whatsappChat.reason.salonNotConnected",
  TEMPLATE_FETCH_FAILED: "whatsappChat.reason.fetchFailed",
  NO_APPROVED_TEMPLATES_ON_WABA: "whatsappChat.reason.noApproved",
};

export const UPLOAD_ERRORS = {
  WINDOW_CLOSED: "whatsappChat.upload.windowClosed",
  MIME_NOT_SUPPORTED: "whatsappChat.upload.mimeNotSupported",
  FILE_TOO_LARGE: "whatsappChat.upload.fileTooLarge",
  WHATSAPP_NOT_CONNECTED: "whatsappChat.upload.senderNotConnected",
  UPLOAD_REJECTED: "whatsappChat.upload.rejected",
  MEDIA_EXPIRED: "whatsappChat.upload.expired",
};
import { Send, MessageSquare, Lock, Sparkles, ChevronDown, Paperclip, Loader2 } from "lucide-react";
import { MessageList } from "@/components/whatsapp/Conversation";

/**
 * WhatsAppChat — timeline embedded in ClientDetail.
 * Shows message history with 24h-window indicator.
 * If window is open: free-form input.
 * If window is closed: template picker required (Meta policy).
 * Sending is STUB until Ondata 1 wires Meta Cloud API.
 */
export function WhatsAppChat({ clientId, clientName, clientPhone }) {
  const { t: tr } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [windowOpen, setWindowOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [body, setBody] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templateLang, setTemplateLang] = useState("");
  const [tplVars, setTplVars] = useState([]);
  const [senderInfo, setSenderInfo] = useState(null);
  const [tplReason, setTplReason] = useState(null);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const sendFile = async (f) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("client_id", clientId);
      fd.append("caption", body.trim());
      fd.append("file", f);
      const res = await api.post("/whatsapp/send-media", fd);
      if (res.data?.status === "error") toast.error(tr("inbox.attachmentRejected"));
      else { setBody(""); toast.success(tr("inbox.attachmentSent")); }
      load();
    } catch (e) {
      toast.error(tr(UPLOAD_ERRORS[e?.response?.data?.detail] || "inbox.attachmentError"));
    } finally { setUploading(false); }
  };

  const load = async () => {
    try {
      const [m, t] = await Promise.all([
        api.get(`/whatsapp/messages/${clientId}`),
        api.get(`/whatsapp/sendable-templates?client_id=${clientId}`).catch(() => ({ data: {} })),
      ]);
      setMessages(m.data.messages || []);
      setWindowOpen(!!m.data.window_open);
      setTemplates(t.data?.templates || []);
      setSenderInfo(t.data?.sender || null);
      setTplReason(t.data?.reason || null);
    } catch (e) {
      // Silent: chat may be empty for a new client
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [clientId]); // eslint-disable-line

  const bodyVarCount = (t) => new Set((t?.variables || [])
    .filter((v) => (v.component || "").toUpperCase() === "BODY")
    .map((v) => v.position)).size;

  const pickTemplate = (key) => {
    const t = templates.find((x) => `${x.name}|${x.language}` === key);
    setTemplateName(t?.name || "");
    setTemplateLang(t?.language || "");
    setTplVars(Array(bodyVarCount(t)).fill(""));
  };

  const send = async () => {
    if (!body.trim() && !templateName) { toast.error(tr("whatsappChat.emptyMessage")); return; }
    if (!windowOpen && !templateName) { toast.error(tr("whatsappChat.templateRequired")); return; }
    if (templateName && tplVars.some((v) => !v.trim())) {
      toast.error(tr("whatsappChat.fillVariables")); return;
    }
    setSending(true);
    try {
      const res = await api.post("/whatsapp/send", {
        client_id: clientId,
        body: body.trim(),
        template_name: templateName || null,
        params: templateName ? {
          language: templateLang,
          ...(tplVars.length ? {
            components: [{ type: "BODY", parameters: tplVars.map((v) => ({ type: "text", text: v })) }],
          } : {}),
        } : null,
      });
      if (res.data?.status === "error") {
        toast.error(res.data?.error === "WHATSAPP_TENANT_NOT_CONNECTED"
          ? tr("whatsappChat.reason.salonNotConnected")
          : tr("whatsappChat.sendRejected"));
      } else {
        setBody(""); setTemplateName(""); setTemplateLang(""); setTplVars([]);
        toast.success(templateName ? tr("whatsappChat.templateSent", { name: templateName }) : tr("whatsappChat.messageSent"));
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || tr("inbox.sendError")); }
    finally { setSending(false); }
  };

  const simulateInbound = async () => {
    const text = window.prompt(tr("whatsappChat.simulatePrompt", { name: clientName }), tr("whatsappChat.simulateExample"));
    if (!text) return;
    try {
      await api.post("/whatsapp/simulate-inbound", { client_id: clientId, body: text });
      toast.success(tr("whatsappChat.simulated"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || tr("common.error")); }
  };

  return (
    <section className="sp-card overflow-hidden flex flex-col" data-testid="whatsapp-chat">
      <div className="p-4 border-b border-[color:var(--sp-border)] flex items-center gap-3 flex-shrink-0 bg-white">
        <div className="h-9 w-9 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
          <MessageSquare className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">WhatsApp</div>
          <div className="text-[11px] text-[color:var(--sp-text-muted)] truncate">
            {clientPhone || tr("whatsappChat.noPhone")}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${windowOpen ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="wa-window-indicator">
            {windowOpen ? (<><Sparkles className="h-3 w-3" /> {tr("whatsappChat.windowOpen")}</>) : (<><Lock className="h-3 w-3" /> {tr("whatsappChat.templateOnly")}</>)}
          </span>
          <button onClick={simulateInbound} title={tr("whatsappChat.simulateTitle")} data-testid="wa-simulate-inbound" className="text-[10px] font-bold text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)] px-2 py-1">
            {tr("whatsappChat.simulateBtn")}
          </button>
        </div>
      </div>

      {/* Message list — stessa esperienza della Inbox (componente condiviso) */}
      <MessageList
        messages={messages}
        testId="wa-messages"
        emptyText={tr("chat.noMessagesYet")}
        className="h-[min(45vh,400px)] flex-none"
      />

      {/* Composer */}
      <div className="p-3 border-t border-[color:var(--sp-border)] space-y-2 bg-white flex-shrink-0">
        {!windowOpen && (
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">
              {tr("whatsappChat.templateLabel")}
            </label>
            <select data-testid="wa-template-select" className="sp-input mt-1 text-xs"
              value={templateName ? `${templateName}|${templateLang}` : ""}
              onChange={(e) => pickTemplate(e.target.value)}>
              <option value="">{tr("whatsappChat.choosTemplate")}</option>
              {templates.map((t) => (
                <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                  {t.name} · {t.language}{t.category ? ` · ${t.category}` : ""}
                </option>
              ))}
            </select>
            {templates.length === 0 && (
              <div className="text-[10px] text-amber-700 mt-1" data-testid="wa-no-templates">
                {tr(TPL_REASON_KEYS[tplReason] || TPL_REASON_KEYS.NO_APPROVED_TEMPLATES_ON_WABA)}
              </div>
            )}
            {tplVars.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="wa-template-vars">
                {tplVars.map((v, i) => (
                  <input key={i} data-testid={`wa-template-var-${i + 1}`} className="sp-input text-xs"
                    placeholder={tr("whatsappChat.variable", { n: i + 1 })} value={v}
                    onChange={(e) => setTplVars(tplVars.map((x, j) => (j === i ? e.target.value : x)))} />
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            type="file" ref={fileRef} className="hidden" data-testid="wa-file-input"
            accept="image/jpeg,image/png,video/mp4,video/3gpp,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) sendFile(f); }}
          />
          <button
            type="button" data-testid="wa-attach-btn"
            onClick={() => fileRef.current?.click()}
            disabled={!windowOpen || uploading}
            title={windowOpen ? tr("inbox.attachTooltip") : tr("inbox.attachWindowClosed")}
            className="p-2 rounded-full hover:bg-slate-100 text-[color:var(--sp-text-muted)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>
          <textarea
            data-testid="wa-composer"
            className="sp-input flex-1 text-sm min-h-[42px] max-h-24"
            placeholder={windowOpen ? tr("inbox.writeMessage") : tr("whatsappChat.selectTemplateAbove")}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
          />
          <button data-testid="wa-send-btn" disabled={sending} onClick={send} className="sp-btn-primary inline-flex items-center gap-1 !px-3 !py-2">
            <Send className="h-4 w-4" />
          </button>
        </div>
        <div className="text-[10px] text-[color:var(--sp-text-muted)]" data-testid="wa-sender-info">
          {senderInfo?.ready === false
            ? tr("whatsappChat.senderNotReady")
            : tr(senderInfo?.owner_type === "agency_receptionist"
                  ? "whatsappChat.sendingFromReceptionist"
                  : "whatsappChat.sendingFromSalon",
                { phone: senderInfo?.display_phone_number ? ` (${senderInfo.display_phone_number})` : "" })}
        </div>
      </div>
    </section>
  );
}
